// Blog article translations - Batch 5
// Articles: guest-reviews-published-website, improved-satisfaction-questionnaires, barcode-scanning-check-in, new-reservation-form, owner-smartphone-app, ical-export-made-easy, plum-guide-channel-integration

export const blogArticleTranslationsBatch5: Record<string, Record<string, string>> = {
  en: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Guest Reviews Now Published on Your Website',
    'blog.article.reviews-website.excerpt': 'Build trust with future guests by automatically displaying positive reviews on your accommodation pages — with full control over what gets published.',
    'blog.article.reviews-website.content': `
## Social Proof That Converts

Following our recent improvements to the satisfaction questionnaires, guest reviews are now automatically published on your i-Rent powered website. This means future visitors can see authentic feedback from previous guests.

## How It Works

### Automatic Publishing

Reviews intended for future tenants are automatically published on your website when **all ratings are 6 or higher**. These appear under the valuation section for each accommodation.

### Two Types of Reviews

Remember, guest comments are now divided into two categories:

1. **Reviews for the rental company** — Private feedback for your internal use
2. **Reviews for future tenants** — Public testimonials displayed on your website

### Full Control

You maintain complete control over what appears on your website. From your i-Rent account, navigate to **Satisfaction Questionnaires** where you can:

- Remove reviews you don't want published
- Publish additional reviews you'd like to share
- Manage both private and public comments

## Why Reviews Matter

- **Build trust** — Authentic guest feedback reassures potential bookers
- **Increase conversions** — Properties with reviews get more bookings
- **Improve SEO** — Fresh, user-generated content helps search rankings
- **Stand out** — Differentiate your properties from competitors

## Get Started

Reviews from your satisfied guests are now working for you 24/7, helping convince future visitors to book. Check your Satisfaction Questionnaires menu to manage your published reviews.
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Improved Guest Satisfaction Questionnaires',
    'blog.article.questionnaires.excerpt': 'Collect better feedback with our enhanced questionnaires — featuring separate comment fields and new questions about travel companions.',
    'blog.article.questionnaires.content': `
## Better Feedback, Better Service

After guests stay at one of your holiday accommodations, you can send them a questionnaire to learn about their experience. These results are valuable for improving your service and can be published on your i-Rent website as guest valuations.

We've made several improvements to make these questionnaires even more useful.

## What's New

### Two Separate Comment Fields

Guests now have two distinct areas for feedback:

1. **Private comments** — Feedback intended only for you, the rental company
2. **Public recommendations** — Comments meant to help future guests

This separation ensures you receive honest operational feedback while also collecting testimonials you can share publicly.

### New Travel Companion Question

We've added a question about who guests traveled with. This information helps you:

- Understand your guest demographics
- Tailor your marketing to your actual audience
- Identify which properties work best for different group types

## Coming Soon

We're working on automatically publishing guest recommendations on your website, giving future visitors authentic testimonials from past guests.

## Benefits

- **Richer feedback** — Separate private and public comments
- **Better insights** — Understand who your guests are
- **Improved marketing** — Use real testimonials on your website
- **Higher satisfaction** — Act on private feedback to improve service
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Speed Up Check-Ins with Barcode Scanning',
    'blog.article.barcode.excerpt': 'Introducing PDF417 barcode support on reservation forms — scan and retrieve booking details in under a second.',
    'blog.article.barcode.content': `
## Faster Check-Ins Are Here

We've added support for **PDF417 2D barcodes** to our reservation forms, dramatically speeding up the check-in process for your guests.

## How It Works

Each reservation form now includes a 2D barcode containing all the booking information. When a guest arrives, simply scan the barcode and the complete reservation opens instantly in i-Rent — no searching or scrolling through lists required.

## Benefits

- **Lightning fast** — Retrieve reservation details in less than a second
- **No manual searching** — Skip scrolling through booking lists
- **Fewer errors** — Eliminate typos when looking up reservations
- **Professional experience** — Impress guests with efficient service

## What You Need

Getting started is simple:

1. **Request the upgrade** — Contact us to enable barcode support
2. **Get a 2D scanner** — Any modern scanner that reads PDF417 codes will work

We tested extensively with the **Motorola DS9208** scanner, but compatible alternatives are widely available.

## Transform Your Check-In Process

Stop wasting time on manual lookups. With barcode scanning, your front desk operations become faster, smoother, and more professional. Contact us to get started!
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Introducing the New Reservation Form',
    'blog.article.reservation-form.excerpt': 'A redesigned reservation form with improved layout, credit card authorization, and barcode scanner support for faster guest check-ins.',
    'blog.article.reservation-form.content': `
## A Better Booking Experience

We're always working to improve our services. One of our latest upgrades is a completely redesigned reservation form that makes the booking process clearer and more professional.

## What's New

### Improved Design

The new reservation form features a modern, clean layout that's easier for guests to read and complete.

### Credit Card Authorization

A dedicated page for authorizing credit card payments has been added, streamlining the payment process and improving security.

### Barcode Scanner Support

The new form includes support for barcode scanners, enabling faster check-ins and reducing manual data entry errors.

## Key Benefits

- **Professional appearance** — Make a great impression with clean, modern documents
- **Clearer information** — Guests can easily understand booking details
- **Faster processing** — Barcode scanning speeds up check-in procedures
- **Secure payments** — Dedicated credit card authorization page

## Important Note

To ensure your documents look their best, make sure your **bank data** is organized in two or three lines. You can update your bank details by going to the **Administrator** menu and selecting **Administrator Information**.
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Introducing the i-Rent Owner App for Property Owners',
    'blog.article.owner-app.excerpt': 'Empower your property owners with a dedicated smartphone app — manage calendars, view income, share documents, and communicate directly with your team.',
    'blog.article.owner-app.content': `
## A New Way to Connect with Property Owners

In addition to the web-based login that owners already use within i-Rent, we're excited to introduce a dedicated **smartphone app** designed specifically for property owners.

## Why an Owner App?

Communication between property owners and managers is crucial for a well-organized rental season. With a mobile app on their smartphone, owners can connect directly with their property manager — speeding up information flow and making data more transparent. The result? Stronger owner relationships and smoother operations.

## What Can Owners Do with the App?

### Calendar Management
- Update the calendar with private bookings for personal use
- Block periods when the property is unavailable
- Submit service requests for private stays
- Add guest details for personal bookings

### Occupancy Overview
- View a complete list of all bookings
- Update or cancel owner bookings
- See all arrival and departure dates at a glance

### Discounts
- Post discounts (with manager permission)
- Set percentage-based discounts between specific dates
- Discounts are deducted from the owner's share of rent
- Changes appear immediately on your website

### Revenue Tracking
- Access net rental income statements
- View income organized by season
- Sort and filter as needed

### Document Sharing
- Share documents and photos with the property manager
- Receive files directly on the smartphone
- Forward documents via email from the app

### Messaging
- Send and receive messages with the property manager
- Get notifications for updates and announcements
- All messages sync to the manager's i-Rent dashboard

## Available on iOS and Android

The Owner App is available for both **Apple** and **Android** smartphones, and supports **7 languages**: English, Spanish, German, French, Dutch, Italian, and Portuguese.

**Download now:**
- [App Store (iOS)](https://apps.apple.com/app/i-rent-net-owners-app/id1569601853?platform=iphone)
- [Google Play (Android)](https://play.google.com/store/apps/details?id=net.irent.ownerapp&hl=es_AR&gl=US)
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'Creating and Exporting iCal Is Now Easier Than Ever',
    'blog.article.ical-export.excerpt': 'Sync your i-Rent calendar with any platform using iCal export — generate your calendar link in just a few clicks.',
    'blog.article.ical-export.content': `
## Seamless Calendar Synchronization

The iCal export feature in i-Rent allows you to effortlessly connect your availability calendar with other platforms and websites. Whether you're listing on a niche booking site or sharing availability with partners, iCal provides a universal solution.

With just a few simple steps, you can generate an iCal link directly from i-Rent and share it wherever you need.

## How to Create an iCal Export

Follow these steps to export your calendar:

1. In i-Rent, navigate to the **Accommodations** menu and select **List accommodations**
2. Right-click on the property you want to export
3. Go to **Channel Manager** → **Export iCal**
4. Click **Activate iCal** — and you're done!

## Why Use iCal Export?

- **Universal compatibility** — iCal works with virtually any calendar system
- **Real-time updates** — External platforms automatically pull your latest availability
- **Prevent double bookings** — Keep all your calendars synchronized
- **Flexible publishing** — Connect to platforms without direct API integration

## Start Syncing Today

If you're working with platforms that don't have a direct connection to i-Rent, iCal export is the perfect solution. Set it up once and enjoy automatic calendar synchronization across all your channels.
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'New Channel: Publish Your Properties on Plum Guide',
    'blog.article.plum-guide.excerpt': 'Reach quality-conscious travelers through Plum Guide — a curated platform featuring only the best holiday rentals, now available via i-Rent\'s Channel Manager.',
    'blog.article.plum-guide.content': `
## What Is Plum Guide?

Founded in London in 2016, [Plum Guide](https://www.plumguide.com) has rapidly expanded to offer a curated selection of high-quality holiday rentals across hundreds of destinations in Europe, the UK, and the United States.

Unlike other platforms that list millions of properties, Plum Guide uses a rigorous selection process known as the **Plum Guide Award** to feature only the best homes. This makes it easier for discerning travelers to find and book exceptional accommodations.

## Why List on Plum Guide?

### Premium Positioning
With over 15 million holiday homes listed online across various platforms, standing out can be challenging. Plum Guide's curated approach means your property is presented alongside only the highest-quality rentals — attracting guests who value quality over price.

### Automatic Synchronization
Through a partnership with Bookingpal and i-Rent.net, you can easily publish your listings on Plum Guide with **automatic booking synchronization**. Reservations flow directly into your i-Rent account, eliminating manual work and reducing the risk of double bookings.

### Dedicated Support
Our support team is ready to guide you through the entire process — from initial setup to managing incoming bookings.

## How to Get Started

1. **Contact i-Rent support** to express your interest in Plum Guide
2. **Apply for Plum Guide** through their partner application page
3. **Complete the setup** with assistance from our team
4. **Start receiving bookings** automatically synced to your i-Rent dashboard

## Elevate Your Rental Business

If your properties meet Plum Guide's quality standards, this channel offers an excellent opportunity to reach a premium audience and increase your booking revenue. Contact us today to learn more!
    `,
  },

  es: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Las Reseñas de Huéspedes Ahora Se Publican en Tu Sitio Web',
    'blog.article.reviews-website.excerpt': 'Genera confianza con futuros huéspedes mostrando automáticamente reseñas positivas en las páginas de tus alojamientos — con control total sobre lo que se publica.',
    'blog.article.reviews-website.content': `
## Prueba Social Que Convierte

Tras nuestras recientes mejoras en los cuestionarios de satisfacción, las reseñas de huéspedes ahora se publican automáticamente en tu sitio web con i-Rent. Esto significa que los futuros visitantes pueden ver comentarios auténticos de huéspedes anteriores.

## Cómo Funciona

### Publicación Automática

Las reseñas destinadas a futuros inquilinos se publican automáticamente en tu sitio web cuando **todas las puntuaciones son 6 o superiores**. Estas aparecen en la sección de valoraciones de cada alojamiento.

### Dos Tipos de Reseñas

Recuerda que los comentarios de los huéspedes ahora se dividen en dos categorías:

1. **Reseñas para la empresa de alquiler** — Comentarios privados para tu uso interno
2. **Reseñas para futuros inquilinos** — Testimonios públicos mostrados en tu sitio web

### Control Total

Mantienes el control completo sobre lo que aparece en tu sitio web. Desde tu cuenta de i-Rent, navega a **Cuestionarios de Satisfacción** donde puedes:

- Eliminar reseñas que no quieras publicar
- Publicar reseñas adicionales que desees compartir
- Gestionar comentarios privados y públicos

## Por Qué Importan las Reseñas

- **Genera confianza** — Los comentarios auténticos tranquilizan a los potenciales clientes
- **Aumenta conversiones** — Las propiedades con reseñas obtienen más reservas
- **Mejora el SEO** — El contenido fresco generado por usuarios ayuda en los rankings de búsqueda
- **Destaca** — Diferencia tus propiedades de la competencia
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Cuestionarios de Satisfacción de Huéspedes Mejorados',
    'blog.article.questionnaires.excerpt': 'Recopila mejores comentarios con nuestros cuestionarios mejorados — con campos de comentarios separados y nuevas preguntas sobre compañeros de viaje.',
    'blog.article.questionnaires.content': `
## Mejor Feedback, Mejor Servicio

Después de que los huéspedes se alojen en uno de tus alojamientos vacacionales, puedes enviarles un cuestionario para conocer su experiencia. Estos resultados son valiosos para mejorar tu servicio y pueden publicarse en tu sitio web de i-Rent como valoraciones de huéspedes.

Hemos realizado varias mejoras para hacer estos cuestionarios aún más útiles.

## Novedades

### Dos Campos de Comentarios Separados

Los huéspedes ahora tienen dos áreas distintas para comentarios:

1. **Comentarios privados** — Feedback destinado solo para ti, la empresa de alquiler
2. **Recomendaciones públicas** — Comentarios pensados para ayudar a futuros huéspedes

Esta separación asegura que recibas feedback operativo honesto mientras también recopilas testimonios que puedes compartir públicamente.

### Nueva Pregunta sobre Compañeros de Viaje

Hemos añadido una pregunta sobre con quién viajaron los huéspedes. Esta información te ayuda a:

- Entender la demografía de tus huéspedes
- Adaptar tu marketing a tu audiencia real
- Identificar qué propiedades funcionan mejor para diferentes tipos de grupos

## Próximamente

Estamos trabajando en publicar automáticamente las recomendaciones de huéspedes en tu sitio web, dando a los futuros visitantes testimonios auténticos de huéspedes anteriores.
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Acelera los Check-Ins con Escaneo de Códigos de Barras',
    'blog.article.barcode.excerpt': 'Presentamos soporte para códigos de barras PDF417 en formularios de reserva — escanea y recupera los detalles de la reserva en menos de un segundo.',
    'blog.article.barcode.content': `
## Check-Ins Más Rápidos Han Llegado

Hemos añadido soporte para **códigos de barras 2D PDF417** en nuestros formularios de reserva, acelerando drásticamente el proceso de check-in para tus huéspedes.

## Cómo Funciona

Cada formulario de reserva ahora incluye un código de barras 2D que contiene toda la información de la reserva. Cuando llega un huésped, simplemente escanea el código y la reserva completa se abre instantáneamente en i-Rent — sin necesidad de buscar o desplazarse por listas.

## Beneficios

- **Rapidísimo** — Recupera los detalles de la reserva en menos de un segundo
- **Sin búsqueda manual** — Evita desplazarte por listas de reservas
- **Menos errores** — Elimina errores tipográficos al buscar reservas
- **Experiencia profesional** — Impresiona a los huéspedes con un servicio eficiente

## Lo Que Necesitas

Empezar es sencillo:

1. **Solicita la actualización** — Contáctanos para habilitar el soporte de códigos de barras
2. **Consigue un escáner 2D** — Cualquier escáner moderno que lea códigos PDF417 funcionará

Probamos extensivamente con el escáner **Motorola DS9208**, pero hay alternativas compatibles ampliamente disponibles.

## Transforma Tu Proceso de Check-In

Deja de perder tiempo en búsquedas manuales. Con el escaneo de códigos de barras, las operaciones de recepción se vuelven más rápidas, fluidas y profesionales. ¡Contáctanos para empezar!
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Presentamos el Nuevo Formulario de Reserva',
    'blog.article.reservation-form.excerpt': 'Un formulario de reserva rediseñado con diseño mejorado, autorización de tarjeta de crédito y soporte para escáner de códigos de barras para check-ins más rápidos.',
    'blog.article.reservation-form.content': `
## Una Mejor Experiencia de Reserva

Siempre estamos trabajando para mejorar nuestros servicios. Una de nuestras últimas actualizaciones es un formulario de reserva completamente rediseñado que hace el proceso de reserva más claro y profesional.

## Novedades

### Diseño Mejorado

El nuevo formulario de reserva presenta un diseño moderno y limpio que es más fácil de leer y completar para los huéspedes.

### Autorización de Tarjeta de Crédito

Se ha añadido una página dedicada para autorizar pagos con tarjeta de crédito, optimizando el proceso de pago y mejorando la seguridad.

### Soporte para Escáner de Códigos de Barras

El nuevo formulario incluye soporte para escáneres de códigos de barras, permitiendo check-ins más rápidos y reduciendo errores de entrada de datos manual.

## Beneficios Clave

- **Apariencia profesional** — Causa una gran impresión con documentos limpios y modernos
- **Información más clara** — Los huéspedes pueden entender fácilmente los detalles de la reserva
- **Procesamiento más rápido** — El escaneo de códigos de barras acelera los procedimientos de check-in
- **Pagos seguros** — Página dedicada de autorización de tarjeta de crédito

## Nota Importante

Para asegurar que tus documentos se vean lo mejor posible, asegúrate de que tus **datos bancarios** estén organizados en dos o tres líneas. Puedes actualizar tus datos bancarios yendo al menú **Administrador** y seleccionando **Información del Administrador**.
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Presentamos la App i-Rent para Propietarios',
    'blog.article.owner-app.excerpt': 'Empodera a tus propietarios con una app dedicada para smartphone — gestiona calendarios, visualiza ingresos, comparte documentos y comunícate directamente con tu equipo.',
    'blog.article.owner-app.content': `
## Una Nueva Forma de Conectar con los Propietarios

Además del acceso web que los propietarios ya utilizan dentro de i-Rent, nos complace presentar una **app para smartphone** dedicada diseñada específicamente para propietarios.

## ¿Por Qué una App para Propietarios?

La comunicación entre propietarios y gestores es crucial para una temporada de alquiler bien organizada. Con una app móvil en su smartphone, los propietarios pueden conectarse directamente con su gestor — acelerando el flujo de información y haciendo los datos más transparentes. ¿El resultado? Relaciones más sólidas con los propietarios y operaciones más fluidas.

## ¿Qué Pueden Hacer los Propietarios con la App?

### Gestión del Calendario
- Actualizar el calendario con reservas privadas para uso personal
- Bloquear períodos cuando la propiedad no está disponible
- Enviar solicitudes de servicio para estancias privadas
- Añadir detalles de huéspedes para reservas personales

### Visión General de Ocupación
- Ver una lista completa de todas las reservas
- Actualizar o cancelar reservas del propietario
- Ver todas las fechas de llegada y salida de un vistazo

### Descuentos
- Publicar descuentos (con permiso del gestor)
- Establecer descuentos porcentuales entre fechas específicas
- Los descuentos se deducen de la parte del alquiler del propietario
- Los cambios aparecen inmediatamente en tu sitio web

### Seguimiento de Ingresos
- Acceder a estados de ingresos netos de alquiler
- Ver ingresos organizados por temporada
- Ordenar y filtrar según sea necesario

### Compartir Documentos
- Compartir documentos y fotos con el gestor de la propiedad
- Recibir archivos directamente en el smartphone
- Reenviar documentos por email desde la app

### Mensajería
- Enviar y recibir mensajes con el gestor de la propiedad
- Recibir notificaciones de actualizaciones y anuncios
- Todos los mensajes se sincronizan con el panel de i-Rent del gestor

## Disponible en iOS y Android

La App para Propietarios está disponible para smartphones **Apple** y **Android**, y soporta **7 idiomas**: Inglés, Español, Alemán, Francés, Holandés, Italiano y Portugués.
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'Crear y Exportar iCal Ahora Es Más Fácil Que Nunca',
    'blog.article.ical-export.excerpt': 'Sincroniza tu calendario de i-Rent con cualquier plataforma usando la exportación iCal — genera tu enlace de calendario en solo unos clics.',
    'blog.article.ical-export.content': `
## Sincronización de Calendario Sin Problemas

La función de exportación iCal en i-Rent te permite conectar sin esfuerzo tu calendario de disponibilidad con otras plataformas y sitios web. Ya sea que publiques en un sitio de reservas de nicho o compartas disponibilidad con socios, iCal proporciona una solución universal.

Con solo unos simples pasos, puedes generar un enlace iCal directamente desde i-Rent y compartirlo donde lo necesites.

## Cómo Crear una Exportación iCal

Sigue estos pasos para exportar tu calendario:

1. En i-Rent, navega al menú **Alojamientos** y selecciona **Listar alojamientos**
2. Haz clic derecho en la propiedad que quieres exportar
3. Ve a **Channel Manager** → **Exportar iCal**
4. Haz clic en **Activar iCal** — ¡y listo!

## ¿Por Qué Usar Exportación iCal?

- **Compatibilidad universal** — iCal funciona con prácticamente cualquier sistema de calendario
- **Actualizaciones en tiempo real** — Las plataformas externas obtienen automáticamente tu última disponibilidad
- **Previene dobles reservas** — Mantén todos tus calendarios sincronizados
- **Publicación flexible** — Conecta con plataformas sin integración API directa
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'Nuevo Canal: Publica Tus Propiedades en Plum Guide',
    'blog.article.plum-guide.excerpt': 'Llega a viajeros exigentes a través de Plum Guide — una plataforma curada que presenta solo los mejores alquileres vacacionales, ahora disponible a través del Channel Manager de i-Rent.',
    'blog.article.plum-guide.content': `
## ¿Qué Es Plum Guide?

Fundada en Londres en 2016, [Plum Guide](https://www.plumguide.com) se ha expandido rápidamente para ofrecer una selección curada de alquileres vacacionales de alta calidad en cientos de destinos en Europa, el Reino Unido y Estados Unidos.

A diferencia de otras plataformas que listan millones de propiedades, Plum Guide utiliza un riguroso proceso de selección conocido como el **Plum Guide Award** para presentar solo los mejores hogares. Esto facilita que los viajeros exigentes encuentren y reserven alojamientos excepcionales.

## ¿Por Qué Publicar en Plum Guide?

### Posicionamiento Premium
Con más de 15 millones de hogares vacacionales listados en línea en varias plataformas, destacar puede ser un desafío. El enfoque curado de Plum Guide significa que tu propiedad se presenta junto a solo los alquileres de mayor calidad — atrayendo huéspedes que valoran la calidad sobre el precio.

### Sincronización Automática
A través de una asociación con Bookingpal e i-Rent.net, puedes publicar fácilmente tus listados en Plum Guide con **sincronización automática de reservas**. Las reservas fluyen directamente a tu cuenta de i-Rent, eliminando trabajo manual y reduciendo el riesgo de dobles reservas.

### Soporte Dedicado
Nuestro equipo de soporte está listo para guiarte a través de todo el proceso — desde la configuración inicial hasta la gestión de reservas entrantes.

## Cómo Empezar

1. **Contacta el soporte de i-Rent** para expresar tu interés en Plum Guide
2. **Solicita Plum Guide** a través de su página de solicitud de socios
3. **Completa la configuración** con asistencia de nuestro equipo
4. **Comienza a recibir reservas** sincronizadas automáticamente con tu panel de i-Rent
    `,
  },

  de: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Gästebewertungen Werden Jetzt auf Ihrer Website Veröffentlicht',
    'blog.article.reviews-website.excerpt': 'Bauen Sie Vertrauen bei zukünftigen Gästen auf, indem Sie positive Bewertungen automatisch auf Ihren Unterkunftsseiten anzeigen — mit voller Kontrolle darüber, was veröffentlicht wird.',
    'blog.article.reviews-website.content': `
## Social Proof, der Konvertiert

Nach unseren kürzlichen Verbesserungen der Zufriedenheitsfragebögen werden Gästebewertungen jetzt automatisch auf Ihrer i-Rent-Website veröffentlicht. Das bedeutet, dass zukünftige Besucher authentisches Feedback von früheren Gästen sehen können.

## So Funktioniert Es

### Automatische Veröffentlichung

Bewertungen für zukünftige Mieter werden automatisch auf Ihrer Website veröffentlicht, wenn **alle Bewertungen 6 oder höher** sind. Diese erscheinen unter dem Bewertungsbereich für jede Unterkunft.

### Zwei Arten von Bewertungen

Denken Sie daran, dass Gästekommentare jetzt in zwei Kategorien unterteilt sind:

1. **Bewertungen für das Vermietungsunternehmen** — Privates Feedback für Ihren internen Gebrauch
2. **Bewertungen für zukünftige Mieter** — Öffentliche Testimonials auf Ihrer Website

### Volle Kontrolle

Sie behalten die vollständige Kontrolle darüber, was auf Ihrer Website erscheint. Von Ihrem i-Rent-Konto navigieren Sie zu **Zufriedenheitsfragebögen**, wo Sie:

- Bewertungen entfernen können, die Sie nicht veröffentlichen möchten
- Zusätzliche Bewertungen veröffentlichen können, die Sie teilen möchten
- Sowohl private als auch öffentliche Kommentare verwalten können

## Warum Bewertungen Wichtig Sind

- **Vertrauen aufbauen** — Authentisches Gästefeedback beruhigt potenzielle Bucher
- **Konversionen steigern** — Unterkünfte mit Bewertungen erhalten mehr Buchungen
- **SEO verbessern** — Frischer, nutzergenerierter Inhalt hilft bei Suchrankings
- **Herausstechen** — Differenzieren Sie Ihre Unterkünfte von Mitbewerbern
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Verbesserte Gästezufriedenheits-Fragebögen',
    'blog.article.questionnaires.excerpt': 'Sammeln Sie besseres Feedback mit unseren verbesserten Fragebögen — mit separaten Kommentarfeldern und neuen Fragen zu Reisebegleitern.',
    'blog.article.questionnaires.content': `
## Besseres Feedback, Besserer Service

Nachdem Gäste in einer Ihrer Ferienunterkünfte übernachtet haben, können Sie ihnen einen Fragebogen senden, um mehr über ihre Erfahrung zu erfahren. Diese Ergebnisse sind wertvoll für die Verbesserung Ihres Services und können auf Ihrer i-Rent-Website als Gästebewertungen veröffentlicht werden.

Wir haben mehrere Verbesserungen vorgenommen, um diese Fragebögen noch nützlicher zu machen.

## Was Ist Neu

### Zwei Separate Kommentarfelder

Gäste haben jetzt zwei verschiedene Bereiche für Feedback:

1. **Private Kommentare** — Feedback nur für Sie, das Vermietungsunternehmen
2. **Öffentliche Empfehlungen** — Kommentare für zukünftige Gäste

Diese Trennung stellt sicher, dass Sie ehrliches operatives Feedback erhalten und gleichzeitig Testimonials sammeln, die Sie öffentlich teilen können.

### Neue Frage zu Reisebegleitern

Wir haben eine Frage hinzugefügt, mit wem die Gäste gereist sind. Diese Information hilft Ihnen:

- Die Demografie Ihrer Gäste zu verstehen
- Ihr Marketing auf Ihre tatsächliche Zielgruppe abzustimmen
- Zu identifizieren, welche Unterkünfte am besten für verschiedene Gruppentypen funktionieren
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Beschleunigen Sie Check-Ins mit Barcode-Scanning',
    'blog.article.barcode.excerpt': 'Wir stellen PDF417-Barcode-Unterstützung für Reservierungsformulare vor — scannen und rufen Sie Buchungsdetails in unter einer Sekunde ab.',
    'blog.article.barcode.content': `
## Schnellere Check-Ins Sind Da

Wir haben Unterstützung für **PDF417 2D-Barcodes** zu unseren Reservierungsformularen hinzugefügt und beschleunigen damit den Check-in-Prozess für Ihre Gäste drastisch.

## So Funktioniert Es

Jedes Reservierungsformular enthält jetzt einen 2D-Barcode mit allen Buchungsinformationen. Wenn ein Gast ankommt, scannen Sie einfach den Barcode und die vollständige Reservierung öffnet sich sofort in i-Rent — kein Suchen oder Scrollen durch Listen erforderlich.

## Vorteile

- **Blitzschnell** — Rufen Sie Reservierungsdetails in weniger als einer Sekunde ab
- **Keine manuelle Suche** — Überspringen Sie das Scrollen durch Buchungslisten
- **Weniger Fehler** — Eliminieren Sie Tippfehler beim Nachschlagen von Reservierungen
- **Professionelles Erlebnis** — Beeindrucken Sie Gäste mit effizientem Service

## Was Sie Brauchen

Der Einstieg ist einfach:

1. **Fordern Sie das Upgrade an** — Kontaktieren Sie uns, um Barcode-Unterstützung zu aktivieren
2. **Besorgen Sie einen 2D-Scanner** — Jeder moderne Scanner, der PDF417-Codes liest, funktioniert

Wir haben ausgiebig mit dem **Motorola DS9208** Scanner getestet, aber kompatible Alternativen sind weit verbreitet.
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Das Neue Reservierungsformular',
    'blog.article.reservation-form.excerpt': 'Ein neu gestaltetes Reservierungsformular mit verbessertem Layout, Kreditkartenautorisierung und Barcode-Scanner-Unterstützung für schnellere Gäste-Check-ins.',
    'blog.article.reservation-form.content': `
## Ein Besseres Buchungserlebnis

Wir arbeiten ständig daran, unsere Dienste zu verbessern. Eines unserer neuesten Upgrades ist ein komplett neu gestaltetes Reservierungsformular, das den Buchungsprozess klarer und professioneller macht.

## Was Ist Neu

### Verbessertes Design

Das neue Reservierungsformular verfügt über ein modernes, sauberes Layout, das für Gäste einfacher zu lesen und auszufüllen ist.

### Kreditkartenautorisierung

Eine spezielle Seite für die Autorisierung von Kreditkartenzahlungen wurde hinzugefügt, die den Zahlungsprozess optimiert und die Sicherheit verbessert.

### Barcode-Scanner-Unterstützung

Das neue Formular enthält Unterstützung für Barcode-Scanner, was schnellere Check-ins ermöglicht und manuelle Dateneingabefehler reduziert.

## Hauptvorteile

- **Professionelles Erscheinungsbild** — Machen Sie einen großartigen Eindruck mit sauberen, modernen Dokumenten
- **Klarere Informationen** — Gäste können Buchungsdetails leicht verstehen
- **Schnellere Verarbeitung** — Barcode-Scanning beschleunigt Check-in-Verfahren
- **Sichere Zahlungen** — Dedizierte Kreditkartenautorisierungsseite
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Die i-Rent Eigentümer-App für Immobilienbesitzer',
    'blog.article.owner-app.excerpt': 'Befähigen Sie Ihre Eigentümer mit einer dedizierten Smartphone-App — verwalten Sie Kalender, sehen Sie Einnahmen ein, teilen Sie Dokumente und kommunizieren Sie direkt mit Ihrem Team.',
    'blog.article.owner-app.content': `
## Eine Neue Art, mit Eigentümern zu Verbinden

Zusätzlich zum webbasierten Login, den Eigentümer bereits in i-Rent nutzen, freuen wir uns, eine dedizierte **Smartphone-App** vorzustellen, die speziell für Immobilienbesitzer entwickelt wurde.

## Warum Eine Eigentümer-App?

Die Kommunikation zwischen Eigentümern und Verwaltern ist entscheidend für eine gut organisierte Vermietungssaison. Mit einer mobilen App auf ihrem Smartphone können Eigentümer direkt mit ihrem Verwalter verbunden sein — was den Informationsfluss beschleunigt und Daten transparenter macht. Das Ergebnis? Stärkere Eigentümerbeziehungen und reibungslosere Abläufe.

## Was Können Eigentümer mit der App Tun?

### Kalenderverwaltung
- Den Kalender mit privaten Buchungen für persönliche Nutzung aktualisieren
- Zeiträume blockieren, wenn die Unterkunft nicht verfügbar ist
- Serviceanfragen für private Aufenthalte einreichen
- Gästedetails für persönliche Buchungen hinzufügen

### Belegungsübersicht
- Eine vollständige Liste aller Buchungen einsehen
- Eigentümerbuchungen aktualisieren oder stornieren
- Alle An- und Abreisedaten auf einen Blick sehen

### Rabatte
- Rabatte veröffentlichen (mit Verwaltergenehmigung)
- Prozentuale Rabatte zwischen bestimmten Daten festlegen
- Rabatte werden vom Mietanteil des Eigentümers abgezogen
- Änderungen erscheinen sofort auf Ihrer Website

### Einnahmenverfolgung
- Zugang zu Netto-Mieteinnahmenberichten
- Einnahmen nach Saison organisiert einsehen
- Nach Bedarf sortieren und filtern

## Verfügbar für iOS und Android

Die Eigentümer-App ist für **Apple**- und **Android**-Smartphones verfügbar und unterstützt **7 Sprachen**: Englisch, Spanisch, Deutsch, Französisch, Niederländisch, Italienisch und Portugiesisch.
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'iCal Erstellen und Exportieren War Noch Nie Einfacher',
    'blog.article.ical-export.excerpt': 'Synchronisieren Sie Ihren i-Rent-Kalender mit jeder Plattform per iCal-Export — generieren Sie Ihren Kalenderlink in nur wenigen Klicks.',
    'blog.article.ical-export.content': `
## Nahtlose Kalendersynchronisation

Die iCal-Exportfunktion in i-Rent ermöglicht es Ihnen, Ihren Verfügbarkeitskalender mühelos mit anderen Plattformen und Websites zu verbinden. Ob Sie auf einer Nischen-Buchungsseite listen oder Verfügbarkeit mit Partnern teilen — iCal bietet eine universelle Lösung.

Mit nur wenigen einfachen Schritten können Sie einen iCal-Link direkt aus i-Rent generieren und überall teilen, wo Sie ihn brauchen.

## So Erstellen Sie Einen iCal-Export

Folgen Sie diesen Schritten, um Ihren Kalender zu exportieren:

1. In i-Rent navigieren Sie zum Menü **Unterkünfte** und wählen **Unterkünfte auflisten**
2. Rechtsklicken Sie auf die Unterkunft, die Sie exportieren möchten
3. Gehen Sie zu **Channel Manager** → **iCal exportieren**
4. Klicken Sie auf **iCal aktivieren** — und fertig!

## Warum iCal-Export Verwenden?

- **Universelle Kompatibilität** — iCal funktioniert mit praktisch jedem Kalendersystem
- **Echtzeit-Updates** — Externe Plattformen ziehen automatisch Ihre neueste Verfügbarkeit
- **Doppelbuchungen verhindern** — Halten Sie alle Ihre Kalender synchronisiert
- **Flexible Veröffentlichung** — Verbinden Sie sich mit Plattformen ohne direkte API-Integration
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'Neuer Kanal: Veröffentlichen Sie Ihre Unterkünfte auf Plum Guide',
    'blog.article.plum-guide.excerpt': 'Erreichen Sie qualitätsbewusste Reisende über Plum Guide — eine kuratierte Plattform mit nur den besten Ferienvermietungen, jetzt über den i-Rent Channel Manager verfügbar.',
    'blog.article.plum-guide.content': `
## Was Ist Plum Guide?

Gegründet in London im Jahr 2016, hat sich [Plum Guide](https://www.plumguide.com) schnell erweitert, um eine kuratierte Auswahl hochwertiger Ferienvermietungen in Hunderten von Destinationen in Europa, Großbritannien und den USA anzubieten.

Im Gegensatz zu anderen Plattformen, die Millionen von Unterkünften listen, verwendet Plum Guide einen rigorosen Auswahlprozess namens **Plum Guide Award**, um nur die besten Häuser zu präsentieren. Das macht es für anspruchsvolle Reisende einfacher, außergewöhnliche Unterkünfte zu finden und zu buchen.

## Warum Auf Plum Guide Listen?

### Premium-Positionierung
Mit über 15 Millionen Ferienhäusern online auf verschiedenen Plattformen kann es schwierig sein, herauszustechen. Der kuratierte Ansatz von Plum Guide bedeutet, dass Ihre Unterkunft neben nur den qualitativ hochwertigsten Vermietungen präsentiert wird — was Gäste anzieht, die Qualität über Preis stellen.

### Automatische Synchronisation
Durch eine Partnerschaft mit Bookingpal und i-Rent.net können Sie Ihre Listings einfach auf Plum Guide mit **automatischer Buchungssynchronisation** veröffentlichen. Reservierungen fließen direkt in Ihr i-Rent-Konto und eliminieren manuelle Arbeit.

## So Starten Sie

1. **Kontaktieren Sie den i-Rent-Support**, um Ihr Interesse an Plum Guide zu bekunden
2. **Bewerben Sie sich bei Plum Guide** über deren Partner-Bewerbungsseite
3. **Schließen Sie die Einrichtung ab** mit Unterstützung unseres Teams
4. **Beginnen Sie, Buchungen zu erhalten**, die automatisch mit Ihrem i-Rent-Dashboard synchronisiert werden
    `,
  },

  fr: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Les Avis des Clients Sont Maintenant Publiés sur Votre Site Web',
    'blog.article.reviews-website.excerpt': 'Construisez la confiance avec les futurs clients en affichant automatiquement les avis positifs sur vos pages d\'hébergement — avec un contrôle total sur ce qui est publié.',
    'blog.article.reviews-website.content': `
## Preuve Sociale Qui Convertit

Suite à nos récentes améliorations des questionnaires de satisfaction, les avis des clients sont maintenant automatiquement publiés sur votre site web i-Rent. Cela signifie que les futurs visiteurs peuvent voir les commentaires authentiques des clients précédents.

## Comment Ça Marche

### Publication Automatique

Les avis destinés aux futurs locataires sont automatiquement publiés sur votre site web lorsque **toutes les notes sont de 6 ou plus**. Ceux-ci apparaissent dans la section évaluation de chaque hébergement.

### Deux Types d'Avis

Rappelons que les commentaires des clients sont maintenant divisés en deux catégories :

1. **Avis pour la société de location** — Commentaires privés pour votre usage interne
2. **Avis pour les futurs locataires** — Témoignages publics affichés sur votre site web

### Contrôle Total

Vous gardez le contrôle total sur ce qui apparaît sur votre site web. Depuis votre compte i-Rent, naviguez vers **Questionnaires de Satisfaction** où vous pouvez :

- Supprimer les avis que vous ne voulez pas publier
- Publier des avis supplémentaires que vous souhaitez partager
- Gérer les commentaires privés et publics

## Pourquoi les Avis Comptent

- **Construire la confiance** — Les commentaires authentiques rassurent les clients potentiels
- **Augmenter les conversions** — Les propriétés avec des avis obtiennent plus de réservations
- **Améliorer le SEO** — Le contenu frais généré par les utilisateurs aide les classements de recherche
- **Se démarquer** — Différenciez vos propriétés de la concurrence
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Questionnaires de Satisfaction Améliorés',
    'blog.article.questionnaires.excerpt': 'Collectez de meilleurs commentaires avec nos questionnaires améliorés — avec des champs de commentaires séparés et de nouvelles questions sur les compagnons de voyage.',
    'blog.article.questionnaires.content': `
## Meilleur Feedback, Meilleur Service

Après que les clients séjournent dans l'un de vos hébergements de vacances, vous pouvez leur envoyer un questionnaire pour connaître leur expérience. Ces résultats sont précieux pour améliorer votre service et peuvent être publiés sur votre site web i-Rent comme évaluations.

Nous avons apporté plusieurs améliorations pour rendre ces questionnaires encore plus utiles.

## Nouveautés

### Deux Champs de Commentaires Séparés

Les clients ont maintenant deux zones distinctes pour les commentaires :

1. **Commentaires privés** — Feedback destiné uniquement à vous, la société de location
2. **Recommandations publiques** — Commentaires destinés à aider les futurs clients

Cette séparation garantit que vous recevez des commentaires opérationnels honnêtes tout en collectant des témoignages que vous pouvez partager publiquement.

### Nouvelle Question sur les Compagnons de Voyage

Nous avons ajouté une question sur les personnes avec lesquelles les clients ont voyagé. Cette information vous aide à :

- Comprendre la démographie de vos clients
- Adapter votre marketing à votre audience réelle
- Identifier quelles propriétés fonctionnent le mieux pour différents types de groupes
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Accélérez les Check-Ins avec le Scan de Codes-Barres',
    'blog.article.barcode.excerpt': 'Présentation du support des codes-barres PDF417 sur les formulaires de réservation — scannez et récupérez les détails de réservation en moins d\'une seconde.',
    'blog.article.barcode.content': `
## Des Check-Ins Plus Rapides Sont Là

Nous avons ajouté le support des **codes-barres 2D PDF417** à nos formulaires de réservation, accélérant considérablement le processus de check-in pour vos clients.

## Comment Ça Marche

Chaque formulaire de réservation inclut maintenant un code-barres 2D contenant toutes les informations de réservation. Quand un client arrive, scannez simplement le code-barres et la réservation complète s'ouvre instantanément dans i-Rent — pas besoin de chercher ou de faire défiler des listes.

## Avantages

- **Ultra rapide** — Récupérez les détails de réservation en moins d'une seconde
- **Pas de recherche manuelle** — Évitez de faire défiler les listes de réservations
- **Moins d'erreurs** — Éliminez les fautes de frappe lors de la recherche de réservations
- **Expérience professionnelle** — Impressionnez les clients avec un service efficace

## Ce Dont Vous Avez Besoin

Commencer est simple :

1. **Demandez la mise à niveau** — Contactez-nous pour activer le support des codes-barres
2. **Procurez-vous un scanner 2D** — Tout scanner moderne qui lit les codes PDF417 fonctionnera
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Présentation du Nouveau Formulaire de Réservation',
    'blog.article.reservation-form.excerpt': 'Un formulaire de réservation repensé avec une mise en page améliorée, l\'autorisation de carte de crédit et le support du scanner de codes-barres pour des check-ins plus rapides.',
    'blog.article.reservation-form.content': `
## Une Meilleure Expérience de Réservation

Nous travaillons toujours à améliorer nos services. L'une de nos dernières mises à niveau est un formulaire de réservation complètement repensé qui rend le processus de réservation plus clair et plus professionnel.

## Nouveautés

### Design Amélioré

Le nouveau formulaire de réservation présente une mise en page moderne et épurée qui est plus facile à lire et à remplir pour les clients.

### Autorisation de Carte de Crédit

Une page dédiée pour l'autorisation des paiements par carte de crédit a été ajoutée, rationalisant le processus de paiement et améliorant la sécurité.

### Support du Scanner de Codes-Barres

Le nouveau formulaire inclut le support des scanners de codes-barres, permettant des check-ins plus rapides et réduisant les erreurs de saisie manuelle.

## Avantages Clés

- **Apparence professionnelle** — Faites une excellente impression avec des documents propres et modernes
- **Informations plus claires** — Les clients peuvent facilement comprendre les détails de réservation
- **Traitement plus rapide** — Le scan de codes-barres accélère les procédures de check-in
- **Paiements sécurisés** — Page dédiée d'autorisation de carte de crédit
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Présentation de l\'Application i-Rent pour les Propriétaires',
    'blog.article.owner-app.excerpt': 'Donnez du pouvoir à vos propriétaires avec une application smartphone dédiée — gérez les calendriers, consultez les revenus, partagez des documents et communiquez directement avec votre équipe.',
    'blog.article.owner-app.content': `
## Une Nouvelle Façon de Connecter avec les Propriétaires

En plus de la connexion web que les propriétaires utilisent déjà dans i-Rent, nous sommes ravis de présenter une **application smartphone** dédiée conçue spécifiquement pour les propriétaires.

## Pourquoi une Application Propriétaire ?

La communication entre les propriétaires et les gestionnaires est cruciale pour une saison de location bien organisée. Avec une application mobile sur leur smartphone, les propriétaires peuvent se connecter directement avec leur gestionnaire — accélérant le flux d'informations et rendant les données plus transparentes. Le résultat ? Des relations plus solides avec les propriétaires et des opérations plus fluides.

## Que Peuvent Faire les Propriétaires avec l'Application ?

### Gestion du Calendrier
- Mettre à jour le calendrier avec des réservations privées pour usage personnel
- Bloquer les périodes où la propriété n'est pas disponible
- Soumettre des demandes de service pour les séjours privés
- Ajouter les détails des clients pour les réservations personnelles

### Aperçu de l'Occupation
- Voir une liste complète de toutes les réservations
- Mettre à jour ou annuler les réservations du propriétaire
- Voir toutes les dates d'arrivée et de départ d'un coup d'œil

### Remises
- Publier des remises (avec la permission du gestionnaire)
- Définir des remises en pourcentage entre des dates spécifiques
- Les remises sont déduites de la part du loyer du propriétaire
- Les modifications apparaissent immédiatement sur votre site web

## Disponible sur iOS et Android

L'Application Propriétaire est disponible pour les smartphones **Apple** et **Android**, et prend en charge **7 langues** : Anglais, Espagnol, Allemand, Français, Néerlandais, Italien et Portugais.
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'Créer et Exporter iCal N\'a Jamais Été Aussi Simple',
    'blog.article.ical-export.excerpt': 'Synchronisez votre calendrier i-Rent avec n\'importe quelle plateforme grâce à l\'export iCal — générez votre lien de calendrier en quelques clics.',
    'blog.article.ical-export.content': `
## Synchronisation de Calendrier Sans Effort

La fonction d'export iCal dans i-Rent vous permet de connecter facilement votre calendrier de disponibilité avec d'autres plateformes et sites web. Que vous publiez sur un site de réservation de niche ou que vous partagez la disponibilité avec des partenaires, iCal fournit une solution universelle.

En quelques étapes simples, vous pouvez générer un lien iCal directement depuis i-Rent et le partager où vous en avez besoin.

## Comment Créer un Export iCal

Suivez ces étapes pour exporter votre calendrier :

1. Dans i-Rent, naviguez vers le menu **Hébergements** et sélectionnez **Lister les hébergements**
2. Faites un clic droit sur la propriété que vous voulez exporter
3. Allez dans **Channel Manager** → **Exporter iCal**
4. Cliquez sur **Activer iCal** — et c'est fait !

## Pourquoi Utiliser l'Export iCal ?

- **Compatibilité universelle** — iCal fonctionne avec pratiquement n'importe quel système de calendrier
- **Mises à jour en temps réel** — Les plateformes externes récupèrent automatiquement votre dernière disponibilité
- **Prévenir les doubles réservations** — Gardez tous vos calendriers synchronisés
- **Publication flexible** — Connectez-vous à des plateformes sans intégration API directe
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'Nouveau Canal : Publiez Vos Propriétés sur Plum Guide',
    'blog.article.plum-guide.excerpt': 'Atteignez les voyageurs soucieux de qualité via Plum Guide — une plateforme sélectionnée présentant uniquement les meilleures locations de vacances, maintenant disponible via le Channel Manager i-Rent.',
    'blog.article.plum-guide.content': `
## Qu'est-ce que Plum Guide ?

Fondée à Londres en 2016, [Plum Guide](https://www.plumguide.com) s'est rapidement développée pour offrir une sélection curatée de locations de vacances de haute qualité dans des centaines de destinations en Europe, au Royaume-Uni et aux États-Unis.

Contrairement à d'autres plateformes qui listent des millions de propriétés, Plum Guide utilise un processus de sélection rigoureux connu sous le nom de **Plum Guide Award** pour ne présenter que les meilleures maisons.

## Pourquoi Publier sur Plum Guide ?

### Positionnement Premium
Avec plus de 15 millions de maisons de vacances listées en ligne sur diverses plateformes, se démarquer peut être un défi. L'approche sélectionnée de Plum Guide signifie que votre propriété est présentée aux côtés des locations de la plus haute qualité — attirant des clients qui valorisent la qualité plutôt que le prix.

### Synchronisation Automatique
Grâce à un partenariat avec Bookingpal et i-Rent.net, vous pouvez facilement publier vos annonces sur Plum Guide avec une **synchronisation automatique des réservations**. Les réservations arrivent directement dans votre compte i-Rent, éliminant le travail manuel.

## Comment Commencer

1. **Contactez le support i-Rent** pour exprimer votre intérêt pour Plum Guide
2. **Postulez chez Plum Guide** via leur page de candidature partenaire
3. **Complétez la configuration** avec l'aide de notre équipe
4. **Commencez à recevoir des réservations** automatiquement synchronisées avec votre tableau de bord i-Rent
    `,
  },

  nl: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Gastbeoordelingen Worden Nu Gepubliceerd op Je Website',
    'blog.article.reviews-website.excerpt': 'Bouw vertrouwen op bij toekomstige gasten door automatisch positieve beoordelingen te tonen op je accommodatiepagina\'s — met volledige controle over wat er wordt gepubliceerd.',
    'blog.article.reviews-website.content': `
## Sociale Bewijskracht Die Converteert

Na onze recente verbeteringen aan de tevredenheidsvragenlijsten worden gastbeoordelingen nu automatisch gepubliceerd op je i-Rent website. Dit betekent dat toekomstige bezoekers authentieke feedback van eerdere gasten kunnen zien.

## Hoe Het Werkt

### Automatische Publicatie

Beoordelingen bedoeld voor toekomstige huurders worden automatisch gepubliceerd op je website wanneer **alle beoordelingen 6 of hoger** zijn. Deze verschijnen onder de waarderingssectie voor elke accommodatie.

### Twee Soorten Beoordelingen

Onthoud dat gastcommentaren nu in twee categorieën zijn verdeeld:

1. **Beoordelingen voor het verhuurbedrijf** — Privé feedback voor intern gebruik
2. **Beoordelingen voor toekomstige huurders** — Openbare getuigenissen op je website

### Volledige Controle

Je behoudt volledige controle over wat er op je website verschijnt. Vanuit je i-Rent account, navigeer naar **Tevredenheidsvragenlijsten** waar je kunt:

- Beoordelingen verwijderen die je niet wilt publiceren
- Extra beoordelingen publiceren die je wilt delen
- Zowel privé als openbare commentaren beheren

## Waarom Beoordelingen Belangrijk Zijn

- **Vertrouwen opbouwen** — Authentieke gastfeedback stelt potentiële boekingen gerust
- **Conversies verhogen** — Accommodaties met beoordelingen krijgen meer boekingen
- **SEO verbeteren** — Verse, door gebruikers gegenereerde content helpt bij zoekrankings
- **Opvallen** — Onderscheid je accommodaties van concurrenten
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Verbeterde Gasttevredenheidsvragenlijsten',
    'blog.article.questionnaires.excerpt': 'Verzamel betere feedback met onze verbeterde vragenlijsten — met aparte commentaarvelden en nieuwe vragen over reisgenoten.',
    'blog.article.questionnaires.content': `
## Betere Feedback, Betere Service

Nadat gasten in een van je vakantieaccommodaties hebben verbleven, kun je hen een vragenlijst sturen om meer over hun ervaring te weten te komen. Deze resultaten zijn waardevol voor het verbeteren van je service en kunnen op je i-Rent website worden gepubliceerd als gastbeoordelingen.

We hebben verschillende verbeteringen aangebracht om deze vragenlijsten nog nuttiger te maken.

## Wat Is Nieuw

### Twee Aparte Commentaarvelden

Gasten hebben nu twee verschillende gebieden voor feedback:

1. **Privé commentaar** — Feedback alleen bedoeld voor jou, het verhuurbedrijf
2. **Openbare aanbevelingen** — Commentaar bedoeld om toekomstige gasten te helpen

Deze scheiding zorgt ervoor dat je eerlijke operationele feedback ontvangt terwijl je ook getuigenissen verzamelt die je openbaar kunt delen.

### Nieuwe Vraag over Reisgenoten

We hebben een vraag toegevoegd over met wie de gasten hebben gereisd. Deze informatie helpt je om:

- De demografie van je gasten te begrijpen
- Je marketing af te stemmen op je werkelijke doelgroep
- Te identificeren welke accommodaties het beste werken voor verschillende groepstypes
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Versnel Check-Ins met Barcode Scanning',
    'blog.article.barcode.excerpt': 'Introductie van PDF417 barcode ondersteuning op reserveringsformulieren — scan en haal boekingsdetails op in minder dan een seconde.',
    'blog.article.barcode.content': `
## Snellere Check-Ins Zijn Hier

We hebben ondersteuning voor **PDF417 2D barcodes** toegevoegd aan onze reserveringsformulieren, waardoor het check-in proces voor je gasten drastisch wordt versneld.

## Hoe Het Werkt

Elk reserveringsformulier bevat nu een 2D barcode met alle boekingsinformatie. Wanneer een gast arriveert, scan je gewoon de barcode en de volledige reservering opent direct in i-Rent — geen zoeken of scrollen door lijsten nodig.

## Voordelen

- **Bliksemsnell** — Haal reserveringsdetails op in minder dan een seconde
- **Geen handmatig zoeken** — Sla het scrollen door boekingslijsten over
- **Minder fouten** — Elimineer typefouten bij het opzoeken van reserveringen
- **Professionele ervaring** — Imponeer gasten met efficiënte service

## Wat Je Nodig Hebt

Beginnen is eenvoudig:

1. **Vraag de upgrade aan** — Neem contact met ons op om barcode ondersteuning te activeren
2. **Schaf een 2D scanner aan** — Elke moderne scanner die PDF417 codes leest werkt
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Introductie van het Nieuwe Reserveringsformulier',
    'blog.article.reservation-form.excerpt': 'Een herontworpen reserveringsformulier met verbeterde lay-out, creditcardautorisatie en barcode scanner ondersteuning voor snellere gast check-ins.',
    'blog.article.reservation-form.content': `
## Een Betere Boekingservaring

We werken altijd aan het verbeteren van onze services. Een van onze nieuwste upgrades is een volledig herontworpen reserveringsformulier dat het boekingsproces duidelijker en professioneler maakt.

## Wat Is Nieuw

### Verbeterd Ontwerp

Het nieuwe reserveringsformulier heeft een moderne, schone lay-out die makkelijker te lezen en in te vullen is voor gasten.

### Creditcardautorisatie

Een speciale pagina voor het autoriseren van creditcardbetalingen is toegevoegd, wat het betalingsproces stroomlijnt en de beveiliging verbetert.

### Barcode Scanner Ondersteuning

Het nieuwe formulier bevat ondersteuning voor barcode scanners, wat snellere check-ins mogelijk maakt en handmatige gegevensinvoerfouten vermindert.

## Belangrijkste Voordelen

- **Professionele uitstraling** — Maak een geweldige indruk met schone, moderne documenten
- **Duidelijkere informatie** — Gasten kunnen boekingsdetails gemakkelijk begrijpen
- **Snellere verwerking** — Barcode scanning versnelt check-in procedures
- **Veilige betalingen** — Speciale creditcardautorisatiepagina
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Introductie van de i-Rent Eigenaren App',
    'blog.article.owner-app.excerpt': 'Geef je eigenaren kracht met een speciale smartphone app — beheer kalenders, bekijk inkomsten, deel documenten en communiceer direct met je team.',
    'blog.article.owner-app.content': `
## Een Nieuwe Manier om te Verbinden met Eigenaren

Naast de webgebaseerde login die eigenaren al gebruiken binnen i-Rent, zijn we verheugd een speciale **smartphone app** te introduceren die speciaal is ontworpen voor vastgoedeigenaren.

## Waarom een Eigenaren App?

Communicatie tussen eigenaren en beheerders is cruciaal voor een goed georganiseerd verhuurseizoen. Met een mobiele app op hun smartphone kunnen eigenaren direct verbonden zijn met hun beheerder — wat de informatiestroom versnelt en data transparanter maakt. Het resultaat? Sterkere eigenaarsrelaties en soepelere operaties.

## Wat Kunnen Eigenaren Doen met de App?

### Kalenderbeheer
- De kalender bijwerken met privéboekingen voor persoonlijk gebruik
- Periodes blokkeren wanneer de accommodatie niet beschikbaar is
- Serviceverzoeken indienen voor privéverblijven
- Gastgegevens toevoegen voor persoonlijke boekingen

### Bezettingsoverzicht
- Een complete lijst van alle boekingen bekijken
- Eigenaarboekingen bijwerken of annuleren
- Alle aankomst- en vertrekdatums in één oogopslag zien

### Kortingen
- Kortingen plaatsen (met toestemming van de beheerder)
- Percentagekortingen tussen specifieke datums instellen
- Kortingen worden afgetrokken van het huuraandeel van de eigenaar
- Wijzigingen verschijnen direct op je website

## Beschikbaar voor iOS en Android

De Eigenaren App is beschikbaar voor zowel **Apple** als **Android** smartphones, en ondersteunt **7 talen**: Engels, Spaans, Duits, Frans, Nederlands, Italiaans en Portugees.
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'iCal Aanmaken en Exporteren Is Nu Makkelijker Dan Ooit',
    'blog.article.ical-export.excerpt': 'Synchroniseer je i-Rent kalender met elk platform via iCal export — genereer je kalenderlink in slechts een paar klikken.',
    'blog.article.ical-export.content': `
## Naadloze Kalendersynchronisatie

De iCal exportfunctie in i-Rent stelt je in staat om moeiteloos je beschikbaarheidskalender te verbinden met andere platforms en websites. Of je nu op een niche boekingssite publiceert of beschikbaarheid deelt met partners, iCal biedt een universele oplossing.

Met slechts een paar eenvoudige stappen kun je een iCal link direct vanuit i-Rent genereren en delen waar je maar wilt.

## Hoe Maak Je een iCal Export

Volg deze stappen om je kalender te exporteren:

1. In i-Rent, navigeer naar het menu **Accommodaties** en selecteer **Accommodaties weergeven**
2. Klik met de rechtermuisknop op de accommodatie die je wilt exporteren
3. Ga naar **Channel Manager** → **iCal Exporteren**
4. Klik op **iCal Activeren** — en klaar!

## Waarom iCal Export Gebruiken?

- **Universele compatibiliteit** — iCal werkt met vrijwel elk kalendersysteem
- **Realtime updates** — Externe platforms halen automatisch je nieuwste beschikbaarheid op
- **Dubbele boekingen voorkomen** — Houd al je kalenders gesynchroniseerd
- **Flexibele publicatie** — Verbind met platforms zonder directe API-integratie
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'Nieuw Kanaal: Publiceer Je Accommodaties op Plum Guide',
    'blog.article.plum-guide.excerpt': 'Bereik kwaliteitsbewuste reizigers via Plum Guide — een geselecteerd platform met alleen de beste vakantiewoningen, nu beschikbaar via de i-Rent Channel Manager.',
    'blog.article.plum-guide.content': `
## Wat Is Plum Guide?

Opgericht in Londen in 2016, is [Plum Guide](https://www.plumguide.com) snel uitgebreid om een geselecteerde selectie van hoogwaardige vakantiewoningen aan te bieden in honderden bestemmingen in Europa, het VK en de Verenigde Staten.

In tegenstelling tot andere platforms die miljoenen accommodaties vermelden, gebruikt Plum Guide een streng selectieproces bekend als de **Plum Guide Award** om alleen de beste woningen te presenteren.

## Waarom Op Plum Guide Publiceren?

### Premium Positionering
Met meer dan 15 miljoen vakantiewoningen online op verschillende platforms kan opvallen een uitdaging zijn. De geselecteerde aanpak van Plum Guide betekent dat je accommodatie wordt gepresenteerd naast alleen de hoogste kwaliteit verhuur — waardoor gasten worden aangetrokken die kwaliteit boven prijs stellen.

### Automatische Synchronisatie
Via een partnerschap met Bookingpal en i-Rent.net kun je eenvoudig je vermeldingen op Plum Guide publiceren met **automatische boekingssynchronisatie**. Reserveringen stromen direct naar je i-Rent account, wat handmatig werk elimineert.

## Hoe Te Beginnen

1. **Neem contact op met i-Rent support** om je interesse in Plum Guide te uiten
2. **Solliciteer bij Plum Guide** via hun partner aanmeldingspagina
3. **Voltooi de setup** met hulp van ons team
4. **Begin met het ontvangen van boekingen** automatisch gesynchroniseerd met je i-Rent dashboard
    `,
  },

  it: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Le Recensioni degli Ospiti Ora Pubblicate sul Tuo Sito Web',
    'blog.article.reviews-website.excerpt': 'Costruisci fiducia con i futuri ospiti mostrando automaticamente le recensioni positive sulle pagine dei tuoi alloggi — con pieno controllo su ciò che viene pubblicato.',
    'blog.article.reviews-website.content': `
## Prova Sociale Che Converte

A seguito dei nostri recenti miglioramenti ai questionari di soddisfazione, le recensioni degli ospiti vengono ora pubblicate automaticamente sul tuo sito web i-Rent. Questo significa che i futuri visitatori possono vedere feedback autentici degli ospiti precedenti.

## Come Funziona

### Pubblicazione Automatica

Le recensioni destinate ai futuri inquilini vengono pubblicate automaticamente sul tuo sito web quando **tutte le valutazioni sono 6 o superiori**. Queste appaiono nella sezione valutazioni di ogni alloggio.

### Due Tipi di Recensioni

Ricorda che i commenti degli ospiti sono ora divisi in due categorie:

1. **Recensioni per la società di noleggio** — Feedback privato per uso interno
2. **Recensioni per futuri inquilini** — Testimonianze pubbliche visualizzate sul tuo sito web

### Controllo Completo

Mantieni il controllo completo su ciò che appare sul tuo sito web. Dal tuo account i-Rent, naviga a **Questionari di Soddisfazione** dove puoi:

- Rimuovere le recensioni che non vuoi pubblicare
- Pubblicare recensioni aggiuntive che desideri condividere
- Gestire sia i commenti privati che pubblici

## Perché le Recensioni Contano

- **Costruire fiducia** — Il feedback autentico degli ospiti rassicura i potenziali prenotatori
- **Aumentare le conversioni** — Le proprietà con recensioni ottengono più prenotazioni
- **Migliorare il SEO** — Contenuti freschi generati dagli utenti aiutano i ranking di ricerca
- **Distinguersi** — Differenzia le tue proprietà dalla concorrenza
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Questionari di Soddisfazione degli Ospiti Migliorati',
    'blog.article.questionnaires.excerpt': 'Raccogli feedback migliori con i nostri questionari migliorati — con campi di commento separati e nuove domande sui compagni di viaggio.',
    'blog.article.questionnaires.content': `
## Migliore Feedback, Migliore Servizio

Dopo che gli ospiti soggiornano in uno dei tuoi alloggi vacanza, puoi inviare loro un questionario per conoscere la loro esperienza. Questi risultati sono preziosi per migliorare il tuo servizio e possono essere pubblicati sul tuo sito web i-Rent come valutazioni.

Abbiamo apportato diversi miglioramenti per rendere questi questionari ancora più utili.

## Novità

### Due Campi di Commento Separati

Gli ospiti ora hanno due aree distinte per il feedback:

1. **Commenti privati** — Feedback destinato solo a te, la società di noleggio
2. **Raccomandazioni pubbliche** — Commenti destinati ad aiutare i futuri ospiti

Questa separazione garantisce che ricevi feedback operativo onesto raccogliendo anche testimonianze che puoi condividere pubblicamente.

### Nuova Domanda sui Compagni di Viaggio

Abbiamo aggiunto una domanda su con chi hanno viaggiato gli ospiti. Questa informazione ti aiuta a:

- Comprendere la demografia dei tuoi ospiti
- Adattare il tuo marketing al tuo pubblico reale
- Identificare quali proprietà funzionano meglio per diversi tipi di gruppi
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Velocizza i Check-In con la Scansione dei Codici a Barre',
    'blog.article.barcode.excerpt': 'Introduzione del supporto per codici a barre PDF417 sui moduli di prenotazione — scansiona e recupera i dettagli della prenotazione in meno di un secondo.',
    'blog.article.barcode.content': `
## Check-In Più Veloci Sono Qui

Abbiamo aggiunto il supporto per **codici a barre 2D PDF417** ai nostri moduli di prenotazione, velocizzando drasticamente il processo di check-in per i tuoi ospiti.

## Come Funziona

Ogni modulo di prenotazione ora include un codice a barre 2D contenente tutte le informazioni della prenotazione. Quando arriva un ospite, basta scansionare il codice a barre e la prenotazione completa si apre istantaneamente in i-Rent — nessuna ricerca o scorrimento di liste richiesto.

## Vantaggi

- **Velocissimo** — Recupera i dettagli della prenotazione in meno di un secondo
- **Nessuna ricerca manuale** — Salta lo scorrimento delle liste di prenotazioni
- **Meno errori** — Elimina gli errori di battitura nella ricerca delle prenotazioni
- **Esperienza professionale** — Impressiona gli ospiti con un servizio efficiente
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Presentazione del Nuovo Modulo di Prenotazione',
    'blog.article.reservation-form.excerpt': 'Un modulo di prenotazione ridisegnato con layout migliorato, autorizzazione carta di credito e supporto per scanner di codici a barre per check-in più veloci.',
    'blog.article.reservation-form.content': `
## Una Migliore Esperienza di Prenotazione

Lavoriamo sempre per migliorare i nostri servizi. Uno dei nostri ultimi aggiornamenti è un modulo di prenotazione completamente ridisegnato che rende il processo di prenotazione più chiaro e professionale.

## Novità

### Design Migliorato

Il nuovo modulo di prenotazione presenta un layout moderno e pulito che è più facile da leggere e compilare per gli ospiti.

### Autorizzazione Carta di Credito

È stata aggiunta una pagina dedicata per autorizzare i pagamenti con carta di credito, snellendo il processo di pagamento e migliorando la sicurezza.

### Supporto per Scanner di Codici a Barre

Il nuovo modulo include il supporto per scanner di codici a barre, consentendo check-in più veloci e riducendo gli errori di inserimento dati manuali.

## Vantaggi Principali

- **Aspetto professionale** — Fai un'ottima impressione con documenti puliti e moderni
- **Informazioni più chiare** — Gli ospiti possono facilmente comprendere i dettagli della prenotazione
- **Elaborazione più veloce** — La scansione dei codici a barre accelera le procedure di check-in
- **Pagamenti sicuri** — Pagina dedicata di autorizzazione carta di credito
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Presentazione dell\'App i-Rent per Proprietari',
    'blog.article.owner-app.excerpt': 'Potenzia i tuoi proprietari con un\'app smartphone dedicata — gestisci calendari, visualizza entrate, condividi documenti e comunica direttamente con il tuo team.',
    'blog.article.owner-app.content': `
## Un Nuovo Modo di Connettersi con i Proprietari

Oltre al login web che i proprietari già utilizzano in i-Rent, siamo lieti di presentare un'**app smartphone** dedicata progettata specificamente per i proprietari di immobili.

## Perché un'App per Proprietari?

La comunicazione tra proprietari e gestori è cruciale per una stagione di affitti ben organizzata. Con un'app mobile sul loro smartphone, i proprietari possono connettersi direttamente con il loro gestore — accelerando il flusso di informazioni e rendendo i dati più trasparenti. Il risultato? Relazioni più solide con i proprietari e operazioni più fluide.

## Cosa Possono Fare i Proprietari con l'App?

### Gestione del Calendario
- Aggiornare il calendario con prenotazioni private per uso personale
- Bloccare periodi quando la proprietà non è disponibile
- Inviare richieste di servizio per soggiorni privati
- Aggiungere dettagli degli ospiti per prenotazioni personali

### Panoramica dell'Occupazione
- Visualizzare un elenco completo di tutte le prenotazioni
- Aggiornare o annullare prenotazioni del proprietario
- Vedere tutte le date di arrivo e partenza a colpo d'occhio

### Sconti
- Pubblicare sconti (con permesso del gestore)
- Impostare sconti percentuali tra date specifiche
- Gli sconti vengono detratti dalla quota di affitto del proprietario
- Le modifiche appaiono immediatamente sul tuo sito web

## Disponibile per iOS e Android

L'App Proprietari è disponibile per smartphone **Apple** e **Android**, e supporta **7 lingue**: Inglese, Spagnolo, Tedesco, Francese, Olandese, Italiano e Portoghese.
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'Creare ed Esportare iCal Non È Mai Stato Così Facile',
    'blog.article.ical-export.excerpt': 'Sincronizza il tuo calendario i-Rent con qualsiasi piattaforma usando l\'esportazione iCal — genera il tuo link del calendario in pochi clic.',
    'blog.article.ical-export.content': `
## Sincronizzazione del Calendario Senza Sforzo

La funzione di esportazione iCal in i-Rent ti permette di connettere facilmente il tuo calendario di disponibilità con altre piattaforme e siti web. Che tu stia pubblicando su un sito di prenotazioni di nicchia o condividendo la disponibilità con partner, iCal fornisce una soluzione universale.

Con pochi semplici passaggi, puoi generare un link iCal direttamente da i-Rent e condividerlo dove ti serve.

## Come Creare un'Esportazione iCal

Segui questi passaggi per esportare il tuo calendario:

1. In i-Rent, naviga al menu **Alloggi** e seleziona **Elenco alloggi**
2. Fai clic destro sulla proprietà che vuoi esportare
3. Vai a **Channel Manager** → **Esporta iCal**
4. Fai clic su **Attiva iCal** — e fatto!

## Perché Usare l'Esportazione iCal?

- **Compatibilità universale** — iCal funziona con praticamente qualsiasi sistema di calendario
- **Aggiornamenti in tempo reale** — Le piattaforme esterne recuperano automaticamente la tua ultima disponibilità
- **Prevenire doppie prenotazioni** — Mantieni tutti i tuoi calendari sincronizzati
- **Pubblicazione flessibile** — Connettiti a piattaforme senza integrazione API diretta
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'Nuovo Canale: Pubblica le Tue Proprietà su Plum Guide',
    'blog.article.plum-guide.excerpt': 'Raggiungi viaggiatori attenti alla qualità attraverso Plum Guide — una piattaforma curata che presenta solo i migliori affitti vacanza, ora disponibile tramite il Channel Manager di i-Rent.',
    'blog.article.plum-guide.content': `
## Cos'è Plum Guide?

Fondata a Londra nel 2016, [Plum Guide](https://www.plumguide.com) si è rapidamente espansa per offrire una selezione curata di affitti vacanza di alta qualità in centinaia di destinazioni in Europa, Regno Unito e Stati Uniti.

A differenza di altre piattaforme che elencano milioni di proprietà, Plum Guide utilizza un rigoroso processo di selezione noto come **Plum Guide Award** per presentare solo le migliori case.

## Perché Pubblicare su Plum Guide?

### Posizionamento Premium
Con oltre 15 milioni di case vacanza elencate online su varie piattaforme, distinguersi può essere una sfida. L'approccio curato di Plum Guide significa che la tua proprietà viene presentata accanto solo agli affitti di altissima qualità — attirando ospiti che apprezzano la qualità rispetto al prezzo.

### Sincronizzazione Automatica
Attraverso una partnership con Bookingpal e i-Rent.net, puoi facilmente pubblicare le tue inserzioni su Plum Guide con **sincronizzazione automatica delle prenotazioni**. Le prenotazioni arrivano direttamente nel tuo account i-Rent, eliminando il lavoro manuale.

## Come Iniziare

1. **Contatta il supporto i-Rent** per esprimere il tuo interesse per Plum Guide
2. **Fai domanda a Plum Guide** attraverso la loro pagina di candidatura partner
3. **Completa la configurazione** con assistenza dal nostro team
4. **Inizia a ricevere prenotazioni** automaticamente sincronizzate con la tua dashboard i-Rent
    `,
  },

  pt: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Avaliações de Hóspedes Agora Publicadas no Seu Site',
    'blog.article.reviews-website.excerpt': 'Construa confiança com futuros hóspedes exibindo automaticamente avaliações positivas nas páginas das suas acomodações — com controle total sobre o que é publicado.',
    'blog.article.reviews-website.content': `
## Prova Social Que Converte

Após as nossas melhorias recentes nos questionários de satisfação, as avaliações dos hóspedes são agora publicadas automaticamente no seu site i-Rent. Isso significa que futuros visitantes podem ver feedback autêntico de hóspedes anteriores.

## Como Funciona

### Publicação Automática

As avaliações destinadas a futuros inquilinos são publicadas automaticamente no seu site quando **todas as classificações são 6 ou superiores**. Estas aparecem na secção de avaliação de cada acomodação.

### Dois Tipos de Avaliações

Lembre-se que os comentários dos hóspedes agora estão divididos em duas categorias:

1. **Avaliações para a empresa de aluguer** — Feedback privado para uso interno
2. **Avaliações para futuros inquilinos** — Testemunhos públicos exibidos no seu site

### Controle Total

Mantém controle total sobre o que aparece no seu site. Da sua conta i-Rent, navegue até **Questionários de Satisfação** onde pode:

- Remover avaliações que não quer publicar
- Publicar avaliações adicionais que deseja partilhar
- Gerir comentários privados e públicos

## Porque as Avaliações Importam

- **Construir confiança** — Feedback autêntico tranquiliza potenciais reservadores
- **Aumentar conversões** — Propriedades com avaliações obtêm mais reservas
- **Melhorar SEO** — Conteúdo fresco gerado por utilizadores ajuda nos rankings de pesquisa
- **Destacar-se** — Diferencie as suas propriedades da concorrência
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Questionários de Satisfação de Hóspedes Melhorados',
    'blog.article.questionnaires.excerpt': 'Recolha melhor feedback com os nossos questionários melhorados — com campos de comentário separados e novas perguntas sobre companheiros de viagem.',
    'blog.article.questionnaires.content': `
## Melhor Feedback, Melhor Serviço

Após os hóspedes ficarem numa das suas acomodações de férias, pode enviar-lhes um questionário para saber sobre a experiência deles. Estes resultados são valiosos para melhorar o seu serviço e podem ser publicados no seu site i-Rent como avaliações.

Fizemos várias melhorias para tornar estes questionários ainda mais úteis.

## Novidades

### Dois Campos de Comentário Separados

Os hóspedes agora têm duas áreas distintas para feedback:

1. **Comentários privados** — Feedback destinado apenas a si, a empresa de aluguer
2. **Recomendações públicas** — Comentários destinados a ajudar futuros hóspedes

Esta separação garante que recebe feedback operacional honesto enquanto também recolhe testemunhos que pode partilhar publicamente.

### Nova Pergunta sobre Companheiros de Viagem

Adicionámos uma pergunta sobre com quem os hóspedes viajaram. Esta informação ajuda-o a:

- Compreender a demografia dos seus hóspedes
- Adaptar o seu marketing ao seu público real
- Identificar quais propriedades funcionam melhor para diferentes tipos de grupos
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Acelere Check-Ins com Digitalização de Códigos de Barras',
    'blog.article.barcode.excerpt': 'Apresentamos suporte para códigos de barras PDF417 em formulários de reserva — digitalize e recupere detalhes da reserva em menos de um segundo.',
    'blog.article.barcode.content': `
## Check-Ins Mais Rápidos Chegaram

Adicionámos suporte para **códigos de barras 2D PDF417** aos nossos formulários de reserva, acelerando drasticamente o processo de check-in para os seus hóspedes.

## Como Funciona

Cada formulário de reserva agora inclui um código de barras 2D contendo todas as informações da reserva. Quando um hóspede chega, simplesmente digitalize o código de barras e a reserva completa abre instantaneamente no i-Rent — sem necessidade de pesquisar ou percorrer listas.

## Benefícios

- **Ultrarrápido** — Recupere detalhes da reserva em menos de um segundo
- **Sem pesquisa manual** — Evite percorrer listas de reservas
- **Menos erros** — Elimine erros de digitação ao procurar reservas
- **Experiência profissional** — Impressione os hóspedes com serviço eficiente
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Apresentamos o Novo Formulário de Reserva',
    'blog.article.reservation-form.excerpt': 'Um formulário de reserva redesenhado com layout melhorado, autorização de cartão de crédito e suporte para scanner de códigos de barras para check-ins mais rápidos.',
    'blog.article.reservation-form.content': `
## Uma Melhor Experiência de Reserva

Estamos sempre a trabalhar para melhorar os nossos serviços. Uma das nossas últimas atualizações é um formulário de reserva completamente redesenhado que torna o processo de reserva mais claro e profissional.

## Novidades

### Design Melhorado

O novo formulário de reserva apresenta um layout moderno e limpo que é mais fácil de ler e preencher para os hóspedes.

### Autorização de Cartão de Crédito

Foi adicionada uma página dedicada para autorizar pagamentos com cartão de crédito, simplificando o processo de pagamento e melhorando a segurança.

### Suporte para Scanner de Códigos de Barras

O novo formulário inclui suporte para scanners de códigos de barras, permitindo check-ins mais rápidos e reduzindo erros de entrada de dados manual.

## Principais Benefícios

- **Aparência profissional** — Cause uma ótima impressão com documentos limpos e modernos
- **Informação mais clara** — Os hóspedes podem facilmente compreender os detalhes da reserva
- **Processamento mais rápido** — A digitalização de códigos de barras acelera os procedimentos de check-in
- **Pagamentos seguros** — Página dedicada de autorização de cartão de crédito
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Apresentamos a App i-Rent para Proprietários',
    'blog.article.owner-app.excerpt': 'Capacite os seus proprietários com uma app smartphone dedicada — gira calendários, veja rendimentos, partilhe documentos e comunique diretamente com a sua equipa.',
    'blog.article.owner-app.content': `
## Uma Nova Forma de Conectar com Proprietários

Para além do login web que os proprietários já utilizam no i-Rent, temos o prazer de apresentar uma **app smartphone** dedicada concebida especificamente para proprietários de imóveis.

## Porquê uma App para Proprietários?

A comunicação entre proprietários e gestores é crucial para uma temporada de arrendamento bem organizada. Com uma app móvel no seu smartphone, os proprietários podem conectar-se diretamente com o seu gestor — acelerando o fluxo de informação e tornando os dados mais transparentes. O resultado? Relações mais fortes com proprietários e operações mais fluidas.

## O Que os Proprietários Podem Fazer com a App?

### Gestão de Calendário
- Atualizar o calendário com reservas privadas para uso pessoal
- Bloquear períodos quando a propriedade não está disponível
- Submeter pedidos de serviço para estadias privadas
- Adicionar detalhes de hóspedes para reservas pessoais

### Visão Geral da Ocupação
- Ver uma lista completa de todas as reservas
- Atualizar ou cancelar reservas do proprietário
- Ver todas as datas de chegada e partida de relance

### Descontos
- Publicar descontos (com permissão do gestor)
- Definir descontos percentuais entre datas específicas
- Os descontos são deduzidos da parte do arrendamento do proprietário
- As alterações aparecem imediatamente no seu site

## Disponível para iOS e Android

A App para Proprietários está disponível para smartphones **Apple** e **Android**, e suporta **7 idiomas**: Inglês, Espanhol, Alemão, Francês, Holandês, Italiano e Português.
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'Criar e Exportar iCal Nunca Foi Tão Fácil',
    'blog.article.ical-export.excerpt': 'Sincronize o seu calendário i-Rent com qualquer plataforma usando exportação iCal — gere o seu link de calendário em apenas alguns cliques.',
    'blog.article.ical-export.content': `
## Sincronização de Calendário Sem Esforço

A funcionalidade de exportação iCal no i-Rent permite-lhe conectar facilmente o seu calendário de disponibilidade com outras plataformas e sites. Quer esteja a publicar num site de reservas de nicho ou a partilhar disponibilidade com parceiros, iCal fornece uma solução universal.

Com apenas alguns passos simples, pode gerar um link iCal diretamente do i-Rent e partilhá-lo onde precisar.

## Como Criar uma Exportação iCal

Siga estes passos para exportar o seu calendário:

1. No i-Rent, navegue até ao menu **Acomodações** e selecione **Listar acomodações**
2. Clique com o botão direito na propriedade que deseja exportar
3. Vá a **Channel Manager** → **Exportar iCal**
4. Clique em **Ativar iCal** — e está feito!

## Porquê Usar Exportação iCal?

- **Compatibilidade universal** — iCal funciona com praticamente qualquer sistema de calendário
- **Atualizações em tempo real** — Plataformas externas obtêm automaticamente a sua última disponibilidade
- **Prevenir reservas duplas** — Mantenha todos os seus calendários sincronizados
- **Publicação flexível** — Conecte-se a plataformas sem integração API direta
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'Novo Canal: Publique as Suas Propriedades no Plum Guide',
    'blog.article.plum-guide.excerpt': 'Alcance viajantes conscientes da qualidade através do Plum Guide — uma plataforma curada que apresenta apenas os melhores alugueres de férias, agora disponível através do Channel Manager i-Rent.',
    'blog.article.plum-guide.content': `
## O Que É o Plum Guide?

Fundado em Londres em 2016, o [Plum Guide](https://www.plumguide.com) expandiu-se rapidamente para oferecer uma seleção curada de alugueres de férias de alta qualidade em centenas de destinos na Europa, Reino Unido e Estados Unidos.

Ao contrário de outras plataformas que listam milhões de propriedades, o Plum Guide utiliza um rigoroso processo de seleção conhecido como **Plum Guide Award** para apresentar apenas as melhores casas.

## Porquê Publicar no Plum Guide?

### Posicionamento Premium
Com mais de 15 milhões de casas de férias listadas online em várias plataformas, destacar-se pode ser um desafio. A abordagem curada do Plum Guide significa que a sua propriedade é apresentada ao lado apenas dos alugueres da mais alta qualidade — atraindo hóspedes que valorizam qualidade acima de preço.

### Sincronização Automática
Através de uma parceria com Bookingpal e i-Rent.net, pode facilmente publicar as suas listagens no Plum Guide com **sincronização automática de reservas**. As reservas fluem diretamente para a sua conta i-Rent, eliminando trabalho manual.

## Como Começar

1. **Contacte o suporte i-Rent** para expressar o seu interesse no Plum Guide
2. **Candidate-se ao Plum Guide** através da página de candidatura de parceiros
3. **Complete a configuração** com assistência da nossa equipa
4. **Comece a receber reservas** automaticamente sincronizadas com o seu painel i-Rent
    `,
  },

  dk: {
    // Article 1: guest-reviews-published-website
    'blog.article.reviews-website.title': 'Gæsteanmeldelser Publiceres Nu på Din Hjemmeside',
    'blog.article.reviews-website.excerpt': 'Opbyg tillid hos fremtidige gæster ved automatisk at vise positive anmeldelser på dine indkvarteringssider — med fuld kontrol over hvad der bliver publiceret.',
    'blog.article.reviews-website.content': `
## Social Proof Der Konverterer

Efter vores seneste forbedringer af tilfredshedsspørgeskemaerne bliver gæsteanmeldelser nu automatisk publiceret på din i-Rent hjemmeside. Det betyder, at fremtidige besøgende kan se autentisk feedback fra tidligere gæster.

## Sådan Fungerer Det

### Automatisk Publicering

Anmeldelser beregnet til fremtidige lejere bliver automatisk publiceret på din hjemmeside, når **alle vurderinger er 6 eller højere**. Disse vises under vurderingssektionen for hver indkvartering.

### To Typer Anmeldelser

Husk at gæstekommentarer nu er opdelt i to kategorier:

1. **Anmeldelser til udlejningsfirmaet** — Privat feedback til intern brug
2. **Anmeldelser til fremtidige lejere** — Offentlige udtalelser vist på din hjemmeside

### Fuld Kontrol

Du har fuldstændig kontrol over hvad der vises på din hjemmeside. Fra din i-Rent konto, naviger til **Tilfredshedsspørgeskemaer** hvor du kan:

- Fjerne anmeldelser du ikke vil publicere
- Publicere yderligere anmeldelser du gerne vil dele
- Administrere både private og offentlige kommentarer

## Hvorfor Anmeldelser Er Vigtige

- **Opbyg tillid** — Autentisk gæstefeedback beroliger potentielle bookere
- **Øg konverteringer** — Ejendomme med anmeldelser får flere bookinger
- **Forbedre SEO** — Frisk, brugergenereret indhold hjælper søgerangeringer
- **Skil dig ud** — Differentier dine ejendomme fra konkurrenterne
    `,

    // Article 2: improved-satisfaction-questionnaires
    'blog.article.questionnaires.title': 'Forbedrede Gæstetilfredshedsspørgeskemaer',
    'blog.article.questionnaires.excerpt': 'Indsaml bedre feedback med vores forbedrede spørgeskemaer — med separate kommentarfelter og nye spørgsmål om rejseledsagere.',
    'blog.article.questionnaires.content': `
## Bedre Feedback, Bedre Service

Efter gæster har boet i en af dine ferieindkvarteringer, kan du sende dem et spørgeskema for at lære om deres oplevelse. Disse resultater er værdifulde for at forbedre din service og kan publiceres på din i-Rent hjemmeside som gæstevurderinger.

Vi har foretaget flere forbedringer for at gøre disse spørgeskemaer endnu mere nyttige.

## Nyheder

### To Separate Kommentarfelter

Gæster har nu to forskellige områder til feedback:

1. **Private kommentarer** — Feedback kun beregnet til dig, udlejningsfirmaet
2. **Offentlige anbefalinger** — Kommentarer beregnet til at hjælpe fremtidige gæster

Denne adskillelse sikrer, at du modtager ærlig operationel feedback, mens du også indsamler udtalelser, du kan dele offentligt.

### Nyt Spørgsmål om Rejseledsagere

Vi har tilføjet et spørgsmål om hvem gæsterne rejste med. Denne information hjælper dig med at:

- Forstå demografien for dine gæster
- Tilpasse din markedsføring til din faktiske målgruppe
- Identificere hvilke ejendomme der fungerer bedst for forskellige gruppetyper
    `,

    // Article 3: barcode-scanning-check-in
    'blog.article.barcode.title': 'Fremskynde Check-Ins med Stregkodescanning',
    'blog.article.barcode.excerpt': 'Introduktion af PDF417 stregkodeunderstøttelse på reservationsformularer — scan og hent bookingdetaljer på under et sekund.',
    'blog.article.barcode.content': `
## Hurtigere Check-Ins Er Her

Vi har tilføjet understøttelse af **PDF417 2D stregkoder** til vores reservationsformularer, hvilket drastisk fremskynder check-in processen for dine gæster.

## Sådan Fungerer Det

Hver reservationsformular inkluderer nu en 2D stregkode med alle bookingoplysninger. Når en gæst ankommer, skal du blot scanne stregkoden, og den komplette reservation åbner øjeblikkeligt i i-Rent — ingen søgning eller scrolling gennem lister påkrævet.

## Fordele

- **Lynhurtigt** — Hent reservationsdetaljer på under et sekund
- **Ingen manuel søgning** — Spring over at scrolle gennem bookinglister
- **Færre fejl** — Eliminer tastefejl når du slår reservationer op
- **Professionel oplevelse** — Imponer gæster med effektiv service
    `,

    // Article 4: new-reservation-form
    'blog.article.reservation-form.title': 'Introduktion af Den Nye Reservationsformular',
    'blog.article.reservation-form.excerpt': 'En redesignet reservationsformular med forbedret layout, kreditkortautorisation og stregkodescanner-understøttelse for hurtigere gæste check-ins.',
    'blog.article.reservation-form.content': `
## En Bedre Bookingoplevelse

Vi arbejder altid på at forbedre vores tjenester. En af vores seneste opgraderinger er en fuldstændigt redesignet reservationsformular, der gør bookingprocessen klarere og mere professionel.

## Nyheder

### Forbedret Design

Den nye reservationsformular har et moderne, rent layout, der er lettere for gæster at læse og udfylde.

### Kreditkortautorisation

En dedikeret side til autorisation af kreditkortbetalinger er blevet tilføjet, hvilket strømliner betalingsprocessen og forbedrer sikkerheden.

### Stregkodescanner-Understøttelse

Den nye formular inkluderer understøttelse af stregkodescannere, hvilket muliggør hurtigere check-ins og reducerer manuelle dataindtastningsfejl.

## Vigtigste Fordele

- **Professionelt udseende** — Gør et godt indtryk med rene, moderne dokumenter
- **Klarere information** — Gæster kan nemt forstå bookingdetaljer
- **Hurtigere behandling** — Stregkodescanning fremskynder check-in procedurer
- **Sikre betalinger** — Dedikeret kreditkortautorisationsside
    `,

    // Article 5: owner-smartphone-app
    'blog.article.owner-app.title': 'Introduktion af i-Rent Ejer App til Ejendomsejere',
    'blog.article.owner-app.excerpt': 'Styrk dine ejere med en dedikeret smartphone app — administrer kalendere, se indtægter, del dokumenter og kommuniker direkte med dit team.',
    'blog.article.owner-app.content': `
## En Ny Måde at Forbinde med Ejere

Ud over det webbaserede login, som ejere allerede bruger i i-Rent, er vi glade for at introducere en dedikeret **smartphone app** designet specielt til ejendomsejere.

## Hvorfor en Ejer App?

Kommunikation mellem ejere og administratorer er afgørende for en velorganiseret udlejningssæson. Med en mobil app på deres smartphone kan ejere forbinde direkte med deres administrator — hvilket fremskynder informationsflowet og gør data mere gennemsigtig. Resultatet? Stærkere ejerrelationer og glattere operationer.

## Hvad Kan Ejere Gøre med Appen?

### Kalenderstyring
- Opdatere kalenderen med private bookinger til personlig brug
- Blokere perioder hvor ejendommen ikke er tilgængelig
- Indsende serviceanmodninger for private ophold
- Tilføje gæstedetaljer for personlige bookinger

### Belegningsoversigt
- Se en komplet liste over alle bookinger
- Opdatere eller annullere ejerbookinger
- Se alle ankomst- og afrejsedatoer med et øjekast

### Rabatter
- Publicere rabatter (med administratorens tilladelse)
- Indstille procentbaserede rabatter mellem specifikke datoer
- Rabatter fratrækkes ejerens andel af lejen
- Ændringer vises øjeblikkeligt på din hjemmeside

## Tilgængelig til iOS og Android

Ejer Appen er tilgængelig for både **Apple** og **Android** smartphones, og understøtter **7 sprog**: Engelsk, Spansk, Tysk, Fransk, Hollandsk, Italiensk og Portugisisk.
    `,

    // Article 6: ical-export-made-easy
    'blog.article.ical-export.title': 'Oprettelse og Eksport af iCal Er Nu Nemmere End Nogensinde',
    'blog.article.ical-export.excerpt': 'Synkroniser din i-Rent kalender med enhver platform via iCal eksport — generer dit kalenderlink med blot et par klik.',
    'blog.article.ical-export.content': `
## Problemfri Kalendersynkronisering

iCal eksportfunktionen i i-Rent giver dig mulighed for nemt at forbinde din tilgængelighedskalender med andre platforme og hjemmesider. Uanset om du publicerer på en niche bookingside eller deler tilgængelighed med partnere, giver iCal en universel løsning.

Med blot et par enkle trin kan du generere et iCal link direkte fra i-Rent og dele det hvor du har brug for det.

## Sådan Opretter Du en iCal Eksport

Følg disse trin for at eksportere din kalender:

1. I i-Rent, naviger til menuen **Indkvarteringer** og vælg **Liste indkvarteringer**
2. Højreklik på den ejendom du vil eksportere
3. Gå til **Channel Manager** → **Eksporter iCal**
4. Klik på **Aktiver iCal** — og du er færdig!

## Hvorfor Bruge iCal Eksport?

- **Universel kompatibilitet** — iCal fungerer med praktisk talt ethvert kalendersystem
- **Realtidsopdateringer** — Eksterne platforme henter automatisk din seneste tilgængelighed
- **Forebyg dobbeltbookinger** — Hold alle dine kalendere synkroniserede
- **Fleksibel publicering** — Forbind til platforme uden direkte API-integration
    `,

    // Article 7: plum-guide-channel-integration
    'blog.article.plum-guide.title': 'Ny Kanal: Publicer Dine Ejendomme på Plum Guide',
    'blog.article.plum-guide.excerpt': 'Nå kvalitetsbevidste rejsende gennem Plum Guide — en kurateret platform med kun de bedste ferielejeboliger, nu tilgængelig via i-Rents Channel Manager.',
    'blog.article.plum-guide.content': `
## Hvad Er Plum Guide?

Grundlagt i London i 2016 har [Plum Guide](https://www.plumguide.com) hurtigt udvidet for at tilbyde et kurateret udvalg af ferieudlejninger af høj kvalitet på tværs af hundredvis af destinationer i Europa, Storbritannien og USA.

I modsætning til andre platforme, der lister millioner af ejendomme, bruger Plum Guide en streng udvælgelsesproces kendt som **Plum Guide Award** for kun at præsentere de bedste hjem.

## Hvorfor Publicere på Plum Guide?

### Premium Positionering
Med over 15 millioner feriehuse online på forskellige platforme kan det være udfordrende at skille sig ud. Plum Guides kuraterede tilgang betyder, at din ejendom præsenteres sammen med kun de højeste kvalitetsudlejninger — hvilket tiltrækker gæster, der værdsætter kvalitet over pris.

### Automatisk Synkronisering
Gennem et partnerskab med Bookingpal og i-Rent.net kan du nemt publicere dine opslag på Plum Guide med **automatisk bookingsynkronisering**. Reservationer flyder direkte ind i din i-Rent konto, hvilket eliminerer manuelt arbejde.

## Sådan Kommer Du I Gang

1. **Kontakt i-Rent support** for at udtrykke din interesse for Plum Guide
2. **Ansøg til Plum Guide** gennem deres partneransøgningsside
3. **Fuldfør opsætningen** med assistance fra vores team
4. **Begynd at modtage bookinger** automatisk synkroniseret med dit i-Rent dashboard
    `,
  },
};

export const slugToTranslationKeyBatch5: Record<string, string> = {
  'guest-reviews-published-website': 'blog.article.reviews-website',
  'improved-satisfaction-questionnaires': 'blog.article.questionnaires',
  'barcode-scanning-check-in': 'blog.article.barcode',
  'new-reservation-form': 'blog.article.reservation-form',
  'owner-smartphone-app': 'blog.article.owner-app',
  'ical-export-made-easy': 'blog.article.ical-export',
  'plum-guide-channel-integration': 'blog.article.plum-guide',
};

export const translatedArticleSlugsBatch5 = [
  'guest-reviews-published-website',
  'improved-satisfaction-questionnaires',
  'barcode-scanning-check-in',
  'new-reservation-form',
  'owner-smartphone-app',
  'ical-export-made-easy',
  'plum-guide-channel-integration',
];
